import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { 
  Briefcase, MapPin, Building2, Calendar, Clock, CheckCircle, 
  XCircle, Loader2, FileText, Send, AlertCircle, User, Pencil,
  Filter, Search, History, ChevronDown, ChevronUp, FileSignature
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const STATUT_CONFIG = {
  'à_l_etude': { label: 'À l\'étude', color: 'bg-blue-100 text-blue-700', icon: Clock },
  'entretien_prévu': { label: 'Entretien prévu', color: 'bg-amber-100 text-amber-700', icon: Calendar },
  'embauché': { label: 'Embauché(e)', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  'non_retenu': { label: 'Non retenu(e)', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const GestionCandidatures = () => {
  const [candidatures, setCandidatures] = useState([]);
  const [offres, setOffres] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidature, setSelectedCandidature] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [filterOffre, setFilterOffre] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedHistorique, setExpandedHistorique] = useState({});
  
  // Dialog création de contrat
  const [showContratDialog, setShowContratDialog] = useState(false);
  const [contratForm, setContratForm] = useState({
    apprenant_id: '',
    entreprise_id: '',
    type_contrat: 'CDI',
    poste: '',
    date_debut: '',
    date_fin: '',
    date_signature: new Date().toISOString().split('T')[0],
    duree_heures: '',
    salaire: '',
    horaires: '',
    notes: ''
  });
  const [savingContrat, setSavingContrat] = useState(false);
  
  const [editForm, setEditForm] = useState({
    statut: '',
    notes_intervenant: '',
    date_entretien: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [candidaturesRes, offresRes, entreprisesRes] = await Promise.all([
        api.get('/candidatures'),
        api.get('/offres-emploi'),
        api.get('/entreprises')
      ]);
      setCandidatures(candidaturesRes.data);
      setOffres(offresRes.data);
      setEntreprises(entreprisesRes.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (candidature) => {
    setSelectedCandidature(candidature);
    setEditForm({
      statut: candidature.statut || 'à_l_etude',
      notes_intervenant: candidature.notes_intervenant || '',
      date_entretien: candidature.date_entretien || ''
    });
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    if (!selectedCandidature) return;
    
    try {
      const previousStatut = selectedCandidature.statut;
      await api.put(`/candidatures/${selectedCandidature.id}`, editForm);
      toast.success('Candidature mise à jour. Le bénéficiaire a été notifié.');
      setShowEditDialog(false);
      
      // Si le statut passe à "embauché", ouvrir automatiquement le formulaire de contrat
      if (editForm.statut === 'embauché' && previousStatut !== 'embauché') {
        // Récupérer les infos de l'offre pour pré-remplir le contrat
        const offre = offres.find(o => o.id === selectedCandidature.offre_id);
        
        // Trouver l'entreprise associée
        let entrepriseId = offre?.entreprise_id || '';
        if (!entrepriseId && offre?.entreprise) {
          const ent = entreprises.find(e => e.nom === offre.entreprise);
          entrepriseId = ent?.id || '';
        }
        
        setContratForm({
          apprenant_id: selectedCandidature.apprenant_id,
          entreprise_id: entrepriseId,
          type_contrat: offre?.type_contrat || 'CDI',
          poste: offre?.intitule || selectedCandidature.offre_intitule || '',
          date_debut: '',
          date_fin: '',
          date_signature: new Date().toISOString().split('T')[0],
          duree_heures: '',
          salaire: offre?.salaire ? parseFloat(offre.salaire.replace(/[^\d.]/g, '')) : '',
          horaires: offre?.horaires || '35h/semaine',
          notes: selectedCandidature.date_candidature 
            ? `Suite à candidature du ${new Date(selectedCandidature.date_candidature).toLocaleDateString('fr-FR')}`
            : ''
        });
        
        setShowContratDialog(true);
        toast.info('🎉 Félicitations ! Créez le contrat de travail pour finaliser l\'embauche.');
      }
      
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de mise à jour');
    }
  };
  
  // Sauvegarder le contrat
  const handleSaveContrat = async () => {
    if (!contratForm.apprenant_id || !contratForm.entreprise_id) {
      toast.error('Veuillez sélectionner le bénéficiaire et l\'entreprise');
      return;
    }
    
    setSavingContrat(true);
    try {
      await api.post('/contrats', {
        ...contratForm,
        salaire: contratForm.salaire ? parseFloat(contratForm.salaire) : null,
        duree_heures: contratForm.duree_heures ? parseInt(contratForm.duree_heures) : null,
        statut: 'en_cours'
      });
      toast.success('✅ Contrat créé avec succès !');
      setShowContratDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du contrat');
    } finally {
      setSavingContrat(false);
    }
  };

  // Filtres
  const filteredCandidatures = candidatures.filter(c => {
    if (filterOffre && filterOffre !== 'all' && c.offre_id !== filterOffre) return false;
    if (filterStatut && filterStatut !== 'all' && c.statut !== filterStatut) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchName = `${c.apprenant_prenom} ${c.apprenant_nom}`.toLowerCase().includes(search);
      const matchOffre = (c.offre_intitule || '').toLowerCase().includes(search);
      if (!matchName && !matchOffre) return false;
    }
    return true;
  });

  // Stats
  const stats = {
    total: candidatures.length,
    en_cours: candidatures.filter(c => c.statut === 'à_l_etude').length,
    entretiens: candidatures.filter(c => c.statut === 'entretien_prévu').length,
    embauches: candidatures.filter(c => c.statut === 'embauché').length,
    refus: candidatures.filter(c => c.statut === 'non_retenu').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200 rounded-3xl">
        <CardHeader>
          <CardTitle className="text-2xl text-indigo-900 flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <Send className="w-7 h-7" />
            Gestion des Candidatures
          </CardTitle>
          <p className="text-indigo-700">{candidatures.length} candidature(s) au total</p>
        </CardHeader>
      </Card>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-slate-50 border-slate-200 rounded-2xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            <p className="text-sm text-slate-600">Total</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200 rounded-2xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{stats.en_cours}</p>
            <p className="text-sm text-blue-600">À l'étude</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200 rounded-2xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">{stats.entretiens}</p>
            <p className="text-sm text-amber-600">Entretiens</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200 rounded-2xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-700">{stats.embauches}</p>
            <p className="text-sm text-emerald-600">Embauché(e)s</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200 rounded-2xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-700">{stats.refus}</p>
            <p className="text-sm text-red-600">Non retenus</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtres */}
      <Card className="bg-white border-slate-200 rounded-2xl">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-slate-600 mb-1 block">Rechercher</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Nom du bénéficiaire ou poste..."
                  className="pl-9 rounded-xl"
                />
              </div>
            </div>
            <div className="w-[200px]">
              <Label className="text-slate-600 mb-1 block">Offre</Label>
              <Select value={filterOffre} onValueChange={setFilterOffre}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Toutes les offres" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les offres</SelectItem>
                  {offres.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.intitule_poste}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[180px]">
              <Label className="text-slate-600 mb-1 block">Statut</Label>
              <Select value={filterStatut} onValueChange={setFilterStatut}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Tous les statuts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {Object.entries(STATUT_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(filterOffre && filterOffre !== 'all') || (filterStatut && filterStatut !== 'all') || searchTerm ? (
              <Button 
                variant="ghost" 
                onClick={() => { setFilterOffre('all'); setFilterStatut('all'); setSearchTerm(''); }}
                className="text-slate-500"
              >
                Réinitialiser
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Liste des candidatures */}
      {filteredCandidatures.length === 0 ? (
        <Card className="bg-white border-slate-200 rounded-3xl p-8 text-center">
          <Send className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Aucune candidature trouvée</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredCandidatures.map((candidature) => {
            const config = STATUT_CONFIG[candidature.statut] || STATUT_CONFIG['à_l_etude'];
            const StatusIcon = config.icon;
            
            return (
              <Card 
                key={candidature.id} 
                className="bg-white border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all"
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {/* Badge statut */}
                        <Badge className={config.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {config.label}
                        </Badge>
                        
                        {/* Date entretien si programmé */}
                        {candidature.statut === 'entretien_prévu' && candidature.date_entretien && (
                          <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
                            <Calendar className="w-3 h-3 mr-1" />
                            {new Date(candidature.date_entretien).toLocaleDateString('fr-FR')}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Nom du bénéficiaire */}
                      <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2 mb-1">
                        <User className="w-5 h-5 text-indigo-600" />
                        {candidature.apprenant_prenom} {candidature.apprenant_nom}
                      </h3>
                      
                      {/* Poste */}
                      <p className="text-slate-600 flex items-center gap-1 mb-2">
                        <Briefcase className="w-4 h-4" />
                        {candidature.offre_intitule || 'Offre'}
                        {candidature.offre_entreprise && (
                          <span className="text-slate-400 ml-2">
                            - {candidature.offre_entreprise}
                          </span>
                        )}
                      </p>
                      
                      {/* Infos supplémentaires */}
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                        {candidature.offre_lieu && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {candidature.offre_lieu}
                          </span>
                        )}
                        {candidature.offre_type_contrat && (
                          <Badge variant="outline">{candidature.offre_type_contrat}</Badge>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Postulé le {new Date(candidature.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>

                      {/* Notes */}
                      {candidature.notes_intervenant && (
                        <div className="mt-3 p-2 bg-slate-50 rounded-lg text-sm text-slate-600">
                          <strong>Notes :</strong> {candidature.notes_intervenant}
                        </div>
                      )}
                      
                      {/* Historique des mises à jour */}
                      {candidature.historique && candidature.historique.length > 0 && (
                        <div className="mt-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedHistorique(prev => ({
                              ...prev,
                              [candidature.id]: !prev[candidature.id]
                            }))}
                            className="text-xs text-slate-500 hover:text-slate-700 p-0 h-auto"
                          >
                            <History className="w-3 h-3 mr-1" />
                            Historique ({candidature.historique.length})
                            {expandedHistorique[candidature.id] ? (
                              <ChevronUp className="w-3 h-3 ml-1" />
                            ) : (
                              <ChevronDown className="w-3 h-3 ml-1" />
                            )}
                          </Button>
                          
                          {expandedHistorique[candidature.id] && (
                            <div className="mt-2 space-y-1 pl-4 border-l-2 border-slate-200">
                              {candidature.historique.slice().reverse().map((h, idx) => (
                                <div key={idx} className="text-xs text-slate-500">
                                  <span className="font-medium text-slate-700">
                                    {new Date(h.date).toLocaleString('fr-FR', {
                                      day: '2-digit', month: '2-digit', year: 'numeric',
                                      hour: '2-digit', minute: '2-digit'
                                    })}
                                  </span>
                                  {' - '}
                                  <span className="text-red-600">{STATUT_CONFIG[h.ancien_statut]?.label || h.ancien_statut}</span>
                                  {' → '}
                                  <span className="text-emerald-600">{STATUT_CONFIG[h.nouveau_statut]?.label || h.nouveau_statut}</span>
                                  <span className="text-slate-400"> par {h.modifie_par_nom || h.modifie_par}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <Button
                      onClick={() => handleEdit(candidature)}
                      className="rounded-xl bg-indigo-600 hover:bg-indigo-700"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Modifier
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog Modification */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <Pencil className="w-5 h-5 text-indigo-600" />
              Modifier la candidature
            </DialogTitle>
            <DialogDescription>
              {selectedCandidature && (
                <span>
                  {selectedCandidature.apprenant_prenom} {selectedCandidature.apprenant_nom} - {selectedCandidature.offre_intitule}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Statut de la candidature</Label>
              <Select value={editForm.statut} onValueChange={(v) => setEditForm({...editForm, statut: v})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUT_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        {React.createElement(config.icon, { className: "w-4 h-4" })}
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {editForm.statut === 'entretien_prévu' && (
              <div className="space-y-2">
                <Label>Date de l'entretien</Label>
                <Input
                  type="date"
                  value={editForm.date_entretien}
                  onChange={(e) => setEditForm({...editForm, date_entretien: e.target.value})}
                  className="rounded-xl"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes (visibles par le bénéficiaire)</Label>
              <Textarea
                value={editForm.notes_intervenant}
                onChange={(e) => setEditForm({...editForm, notes_intervenant: e.target.value})}
                placeholder="Ex: Apporter CV + lettre de motivation..."
                className="rounded-xl resize-none"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button onClick={handleSave} className="rounded-xl bg-indigo-600 hover:bg-indigo-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Création de Contrat (après embauche) */}
      <Dialog open={showContratDialog} onOpenChange={setShowContratDialog}>
        <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <FileSignature className="w-6 h-6" />
              Créer le contrat de travail
            </DialogTitle>
            <DialogDescription>
              Le candidat a été embauché ! Remplissez les informations du contrat.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Alerte succès */}
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-emerald-700">Candidature acceptée !</p>
                <p className="text-sm text-emerald-600">Les informations de l'offre ont été pré-remplies ci-dessous.</p>
              </div>
            </div>

            {/* Type de contrat & Poste */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de contrat *</Label>
                <Select 
                  value={contratForm.type_contrat} 
                  onValueChange={(v) => setContratForm({...contratForm, type_contrat: v})}
                >
                  <SelectTrigger className="rounded-xl" data-testid="contrat-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CDI">CDI</SelectItem>
                    <SelectItem value="CDD">CDD</SelectItem>
                    <SelectItem value="Intérim">Intérim</SelectItem>
                    <SelectItem value="Alternance">Alternance</SelectItem>
                    <SelectItem value="Stage">Stage</SelectItem>
                    <SelectItem value="Contrat Pro">Contrat Pro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Entreprise *</Label>
                <Select 
                  value={contratForm.entreprise_id} 
                  onValueChange={(v) => setContratForm({...contratForm, entreprise_id: v})}
                >
                  <SelectTrigger className="rounded-xl" data-testid="contrat-entreprise-select">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {entreprises.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Poste */}
            <div className="space-y-2">
              <Label>Intitulé du poste *</Label>
              <Input
                value={contratForm.poste}
                onChange={(e) => setContratForm({...contratForm, poste: e.target.value})}
                placeholder="Ex: Serveur, Agent de production..."
                className="rounded-xl"
                data-testid="contrat-poste-input"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date de signature</Label>
                <Input
                  type="date"
                  value={contratForm.date_signature}
                  onChange={(e) => setContratForm({...contratForm, date_signature: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={contratForm.date_debut}
                  onChange={(e) => setContratForm({...contratForm, date_debut: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={contratForm.date_fin}
                  onChange={(e) => setContratForm({...contratForm, date_fin: e.target.value})}
                  className="rounded-xl"
                  disabled={contratForm.type_contrat === 'CDI'}
                />
                {contratForm.type_contrat === 'CDI' && (
                  <p className="text-xs text-slate-500">Non applicable pour un CDI</p>
                )}
              </div>
            </div>

            {/* Salaire & Horaires */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Durée (heures)</Label>
                <Input
                  type="number"
                  value={contratForm.duree_heures}
                  onChange={(e) => setContratForm({...contratForm, duree_heures: e.target.value})}
                  placeholder="Ex: 1607"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Salaire (€/mois)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={contratForm.salaire}
                  onChange={(e) => setContratForm({...contratForm, salaire: e.target.value})}
                  placeholder="Ex: 1800"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Horaires</Label>
                <Input
                  value={contratForm.horaires}
                  onChange={(e) => setContratForm({...contratForm, horaires: e.target.value})}
                  placeholder="Ex: 35h/semaine"
                  className="rounded-xl"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes / Commentaires</Label>
              <Textarea
                value={contratForm.notes}
                onChange={(e) => setContratForm({...contratForm, notes: e.target.value})}
                placeholder="Informations complémentaires..."
                className="rounded-xl resize-none"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowContratDialog(false)} 
              className="rounded-xl"
            >
              Plus tard
            </Button>
            <Button 
              onClick={handleSaveContrat} 
              disabled={savingContrat}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-create-contrat-btn"
            >
              {savingContrat ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <FileSignature className="w-4 h-4 mr-2" />
                  Créer le contrat
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionCandidatures;
